import { useState } from 'react';
import { Plus, Euro, CreditCard as Edit2, Trash2, X, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAgriSubsidies } from '../../hooks/useAgriculture';
import { SUBSIDY_TYPE_LABELS, formatEur } from '../../utils/agriEngine';
import type { AgriSubsidy, SubsidyType } from '../../utils/agriEngine';

interface Props { year: number; }

const defaultForm = {
  name: '',
  amount: '',
  theoretical_payment_date: '',
  prudent_payment_date: '',
  subsidy_type: 'pac' as SubsidyType,
  status: 'attendu' as const,
  notes: '',
};

function SubsidyFormModal({ subsidy, year, onClose, onSaved }: { subsidy?: AgriSubsidy | null; year: number; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState(subsidy ? {
    name: subsidy.name, amount: String(subsidy.amount),
    theoretical_payment_date: subsidy.theoretical_payment_date || '',
    prudent_payment_date: subsidy.prudent_payment_date || '',
    subsidy_type: subsidy.subsidy_type, status: subsidy.status, notes: subsidy.notes || '',
  } : defaultForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k: keyof typeof form, v: string) => setForm(f => ({ ...f, [k]: v }));

  async function handleSave() {
    if (!form.name || !form.amount) { setError('Nom et montant obligatoires'); return; }
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSaving(false); return; }
    const payload = {
      user_id: user.id, name: form.name,
      amount: parseFloat(form.amount),
      theoretical_payment_date: form.theoretical_payment_date || null,
      prudent_payment_date: form.prudent_payment_date || null,
      subsidy_type: form.subsidy_type, campaign_year: year, status: form.status, notes: form.notes || null,
    };
    const { error: err } = subsidy
      ? await supabase.from('agri_subsidies').update(payload).eq('id', subsidy.id)
      : await supabase.from('agri_subsidies').insert(payload);
    setSaving(false);
    if (err) { setError(err.message); return; }
    onSaved();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{subsidy ? 'Modifier l\'aide' : 'Nouvelle aide / subvention'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'aide *</label>
            <input type="text" value={form.name} onChange={e => set('name', e.target.value)} placeholder="DPB - Droits à paiement de base"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Montant attendu (€) *</label>
              <input type="number" min="0" step="100" value={form.amount} onChange={e => set('amount', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={form.subsidy_type} onChange={e => set('subsidy_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm">
                {Object.entries(SUBSIDY_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date théorique</label>
              <input type="date" value={form.theoretical_payment_date} onChange={e => set('theoretical_payment_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date prudente (+2 mois)</label>
              <input type="date" value={form.prudent_payment_date} onChange={e => set('prudent_payment_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
            <select value={form.status} onChange={e => set('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm">
              <option value="attendu">Attendu</option>
              <option value="recu">Reçu</option>
              <option value="annule">Annulé</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm resize-none" />
          </div>
        </div>
        <div className="flex gap-3 p-6 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm">Annuler</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50">
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SubsidiesTab({ year }: Props) {
  const { subsidies, loading, refresh } = useAgriSubsidies(year);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AgriSubsidy | null>(null);

  const totalExpected = subsidies.filter(s => s.status === 'attendu').reduce((sum, s) => sum + s.amount, 0);
  const totalReceived = subsidies.filter(s => s.status === 'recu').reduce((sum, s) => sum + s.amount, 0);
  const totalOptimistic = subsidies.filter(s => s.status !== 'annule').reduce((sum, s) => sum + s.amount, 0);

  async function handleDelete(id: string) {
    if (!confirm('Supprimer cette aide ?')) return;
    await supabase.from('agri_subsidies').delete().eq('id', id);
    refresh();
  }

  if (loading) return <div className="flex items-center justify-center py-16"><div className="animate-spin w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Aides & subventions {year}</h2>
          <p className="text-sm text-gray-500">PAC, MAEC, aides régionales</p>
        </div>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors text-sm font-medium shadow-sm">
          <Plus className="w-4 h-4" />
          Nouvelle aide
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 rounded-xl p-4 border border-green-100">
          <p className="text-xs text-green-600 font-medium mb-1">Total attendu</p>
          <p className="text-2xl font-bold text-green-900">{formatEur(totalOptimistic)}</p>
          <p className="text-xs text-green-600 mt-1">{subsidies.filter(s => s.status !== 'annule').length} aides</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <p className="text-xs text-blue-600 font-medium mb-1">En attente</p>
          <p className="text-2xl font-bold text-blue-900">{formatEur(totalExpected)}</p>
          <p className="text-xs text-blue-600 mt-1">{subsidies.filter(s => s.status === 'attendu').length} aides</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
          <p className="text-xs text-emerald-600 font-medium mb-1">Reçu</p>
          <p className="text-2xl font-bold text-emerald-900">{formatEur(totalReceived)}</p>
          <p className="text-xs text-emerald-600 mt-1">{subsidies.filter(s => s.status === 'recu').length} aides</p>
        </div>
      </div>

      {totalExpected > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-amber-800">Scénario prudent : planifiez avec les dates prudentes</p>
            <p className="text-amber-700 mt-1">Les aides PAC peuvent être versées avec 2 à 3 mois de retard. Utilisez les dates prudentes pour votre plan de trésorerie.</p>
          </div>
        </div>
      )}

      {subsidies.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
          <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Euro className="w-8 h-8 text-green-400" />
          </div>
          <h3 className="text-gray-700 font-medium mb-2">Aucune aide enregistrée</h3>
          <p className="text-gray-400 text-sm mb-6">Ajoutez vos DPB, MAEC et aides régionales pour anticiper vos encaissements</p>
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm font-medium">
            <Plus className="w-4 h-4" />
            Ajouter une aide
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-3 text-gray-500 font-medium">Aide</th>
                <th className="text-left px-4 py-3 text-gray-500 font-medium">Type</th>
                <th className="text-left px-4 py-3 text-gray-500 font-medium">Date théorique</th>
                <th className="text-left px-4 py-3 text-gray-500 font-medium">Date prudente</th>
                <th className="text-right px-4 py-3 text-gray-500 font-medium">Montant</th>
                <th className="text-center px-4 py-3 text-gray-500 font-medium">Statut</th>
                <th className="w-20 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {subsidies.map(s => (
                <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3.5 font-medium text-gray-800">{s.name}</td>
                  <td className="px-4 py-3.5 text-gray-500">{SUBSIDY_TYPE_LABELS[s.subsidy_type]}</td>
                  <td className="px-4 py-3.5 text-gray-600">
                    {s.theoretical_payment_date ? new Date(s.theoretical_payment_date).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td className="px-4 py-3.5 text-amber-600 font-medium">
                    {s.prudent_payment_date ? new Date(s.prudent_payment_date).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td className="px-4 py-3.5 text-right font-semibold text-green-700">{formatEur(s.amount)}</td>
                  <td className="px-4 py-3.5 text-center">
                    {s.status === 'recu' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <CheckCircle2 className="w-3 h-3" /> Reçu
                      </span>
                    ) : s.status === 'attendu' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        <Clock className="w-3 h-3" /> Attendu
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs font-medium">
                        Annulé
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => setEditing(s)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(s.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(showForm || editing) && (
        <SubsidyFormModal
          subsidy={editing}
          year={year}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}
